/**
 * HUD - Main Heads-Up Display component that combines all HUD elements
 */

import React, { useState, useEffect } from 'react';
import { HealthBar } from './HealthBar';
import { StaminaBar } from './StaminaBar';
import { BatteryMeter } from './BatteryMeter';
import { MiniMap } from './MiniMap';
import { Objectives } from './Objectives';
import { Clock } from './Clock';
import { NightProgressBar } from './NightProgressBar';
import { InteractionPrompt } from './InteractionPrompt';
import { NotificationDisplay } from './NotificationDisplay';
import { getInteractionManager } from '../../engine/InteractionManager';
import type { InteractionPrompt as InteractionPromptType } from '../../engine/InteractionManager';

interface HUDProps {
  showHealth?: boolean;
  showStamina?: boolean;
  showBattery?: boolean;
  showMiniMap?: boolean;
  showObjectives?: boolean;
  showClock?: boolean;
  showProgress?: boolean;
  showInteractionPrompt?: boolean;
  nightNumber?: number;
  nightProgress?: number; // 0-100 percentage
}

export const HUD: React.FC<HUDProps> = ({
  showHealth = true,
  showStamina = true,
  showBattery = true,
  showMiniMap = true,
  showObjectives = true,
  showClock = true,
  showProgress = true,
  showInteractionPrompt = true,
  nightNumber = 1,
  nightProgress = 0
}) => {
  const [interactionPrompt, setInteractionPrompt] = useState<InteractionPromptType | null>(null);

  useEffect(() => {
    if (!showInteractionPrompt) {
      return;
    }

    const interactionManager = getInteractionManager();

    // Set up callback for prompt changes
    interactionManager.setOnPromptChange((prompt) => {
      setInteractionPrompt(prompt);
    });

    // Cleanup
    return () => {
      interactionManager.setOnPromptChange(() => {});
    };
  }, [showInteractionPrompt]);

  return (
    <div style={{
      position: 'absolute',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      pointerEvents: 'none',
      zIndex: 100,
    }}>
      {/* Clock - Top center */}
      {showClock && (
        <Clock
          position="top-center"
          nightNumber={nightNumber}
          nightProgress={nightProgress}
        />
      )}

      {/* Notifications - Upper right, between health bar and clock */}
      <NotificationDisplay />

      {/* Health bar - Top left */}
      {showHealth && (
        <HealthBar position="top-left" />
      )}

      {/* Stamina bar - Below health bar */}
      {showStamina && (
        <StaminaBar position="top-left" />
      )}

      {/* Battery meter - Top right */}
      {showBattery && (
        <BatteryMeter position="top-right" />
      )}

      {/* Objectives - Below health bar */}
      {showObjectives && (
        <Objectives position="top-left" />
      )}

      {/* Night progress bar - Bottom center */}
      {showProgress && (
        <NightProgressBar progress={nightProgress} position="bottom" />
      )}

      {/* Interaction prompt - Center bottom */}
      {showInteractionPrompt && interactionPrompt && (
        <InteractionPrompt
          message={interactionPrompt.message}
          key={interactionPrompt.key}
          visible={true}
        />
      )}
    </div>
  );
};

export default HUD;
